import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  BrainCircuit, 
  Workflow, 
  LineChart, 
  Wand2,
  Rocket,
  ArrowRight,
  Check,
  ChevronRight,
  Users,
  BarChart,
  Shield,
  Zap,
  Code,
  Database,
  Star,
  Download,
  Upload,
  Settings,
  Clock,
  TrendingUp
} from 'lucide-react';
import LandingHeader from './LandingHeader';

// Animated background component
// Update the Glowing orbs section in AnimatedBackground:
const AnimatedBackground = () => (
  <div className="fixed inset-0 overflow-hidden -z-10">
    <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
    <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-teal-500/10 animate-pulse-slow opacity-40" />
    
    <div className="relative w-full h-full">
      <div className="absolute top-[10%] left-[20%] w-[600px] h-[600px] rounded-full 
                   bg-gradient-to-r from-cyan-500/20 to-blue-500/20 
                   blur-xl animate-orbit-slower mix-blend-screen opacity-40" />
      <div className="absolute top-[30%] right-[15%] w-[400px] h-[400px] rounded-full 
                   bg-gradient-to-r from-blue-500/20 to-teal-500/20 
                   blur-2xl animate-figure-8-slow mix-blend-screen opacity-40" />
      <div className="absolute bottom-[15%] left-[35%] w-[500px] h-[500px] rounded-full 
                   bg-gradient-to-r from-teal-500/20 to-cyan-500/20 
                   blur-xl animate-ellipse-slow mix-blend-screen opacity-40" />
      <div className="absolute top-[50%] left-[10%] w-[300px] h-[300px] rounded-full 
                   bg-gradient-to-r from-blue-400/20 to-cyan-500/20 
                   blur-2xl animate-float-slow mix-blend-screen opacity-40" />
    </div>

    <div className="absolute inset-0 opacity-30" 
         style={{
           backgroundImage: 'radial-gradient(circle at center, cyan 1px, transparent 1px)',
           backgroundSize: '30px 30px',
           backgroundPosition: '0 0, 15px 15px',
           animation: 'particleShift 30s linear infinite'
         }} />
  </div>
);



const TestimonialCard = ({ quote, author, role, company }) => (
  <div className="bg-gray-900/50 backdrop-blur-lg p-4 rounded-lg">
    <div className="flex items-start mb-3">
      {[...Array(5)].map((_, i) => (
        <Star key={i} className="h-4 w-4 text-yellow-400" fill="currentColor" />
      ))}
    </div>
    <p className="text-gray-300 text-sm mb-3">{quote}</p>
    <div>
      <p className="text-white text-sm font-medium">{author}</p>
      <p className="text-gray-400 text-xs">{role}, {company}</p>
    </div>
  </div>
);

// Rest of the code remains similar but with updated styling and content...
// Add sections for Features, How It Works, Use Cases as before...

const LandingPage = () => {
  const technicalFeatures = [
    {
      icon: <Database className="h-6 w-6 text-cyan-400" />,
      title: "Data Processing",
      features: [
        "Automated data cleaning",
        "Missing value imputation",
        "Feature engineering",
        "Outlier detection",
        "Data transformation"
      ]
    },
    {
      icon: <BrainCircuit className="h-6 w-6 text-cyan-400" />,
      title: "Model Training",
      features: [
        "Hyperparameter tuning",
        "Cross-validation",
        "Model selection",
        "Ensemble methods"
      ]
    },
    {
      icon: <LineChart className="h-6 w-6 text-cyan-400" />,
      title: "Evaluation",
      features: [
        "Performance metrics",
        "Model comparison",
        "Feature importance",
        "Error analysis",
        "Validation curves"
      ]
    }
  ];

  const integrationFeatures = [
    {
      title: "Data Sources",
      description: "Connect with CSV, Excel, SQL databases, and cloud storage",
      icon: <Upload />
    },
    {
      title: "Export Options",
      description: "Deploy models as APIs, batch processing, or embedded solutions",
      icon: <Download />
    },
    {
      title: "Custom Pipelines",
      description: "Create reusable workflows and share across your organization",
      icon: <Settings />
    },
    {
      title: "Real-time Monitoring",
      description: "Track model performance and data drift in production",
      icon: <TrendingUp />
    }
  ];

  return (
    
      <div className="min-h-screen relative text-white">
        <AnimatedBackground />
        <LandingHeader />
        
        {/* Hero Section */}
        <div className="relative pt-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
            <div className="text-center">
              <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-cyan-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                Machine Learning
                <br />
                Made Simple
              </h1>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-6">
                Build and deploy production-ready ML models without writing code.
                From data to deployment in minutes, not months.
              </p>
              <div className="flex justify-center">
                <Link to="/register"
                      className="bg-cyan-500 hover:bg-violet-500 text-white px-6 py-2 rounded-md text-sm font-medium transition-colors">
                  Start Free Trial
                </Link>
              </div>
            </div>
          </div>
        </div>
  
        {/* Technical Features Section */}
        <div id="features" className="relative bg-black/50 backdrop-blur-xl py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-2xl font-bold mb-3">Technical Features</h2>
              <p className="text-lg text-gray-400">
                Everything you need for production-ready machine learning
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {technicalFeatures.map((feature, index) => (
                <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-lg p-4">
                  <div className="bg-gray-900/50 rounded-md w-10 h-10 flex items-center justify-center mb-3">
                    {feature.icon}
                  </div>
                  <h3 className="text-base font-semibold mb-3">{feature.title}</h3>
                  <ul className="space-y-1.5">
                    {feature.features.map((item, i) => (
                      <li key={i} className="flex items-center text-gray-400 text-sm">
                        <Check className="h-3 w-3 text-cyan-400 mr-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

      

      {/* Testimonials Section */}
      <div id="testimonials" className="relative bg-black/50 backdrop-blur-xl py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold mb-3">What Our Users Say</h2>
            <p className="text-lg text-gray-400">
              Join thousands of satisfied users worldwide
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <TestimonialCard
              quote="This platform has transformed how we approach ML projects. What used to take months now takes days."
              author="Sarah Chen"
              role="Data Science Lead"
              company="TechCorp"
            />
            <TestimonialCard
              quote="The ease of use and powerful features make this the perfect solution for our ML needs."
              author="Michael Rodriguez"
              role="CTO"
              company="AI Innovations"
            />
            <TestimonialCard
              quote="Outstanding platform that democratizes machine learning for our entire organization."
              author="Emily Watson"
              role="Head of Analytics"
              company="DataDrive"
            />
          </div>
        </div>
      </div>

      {/* Integration Features */}
      <div id="how-it-works" className="relative py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold mb-3">Seamless Integration</h2>
            <p className="text-lg text-gray-400">
              Connect with your existing tools and workflows
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {integrationFeatures.map((feature, index) => (
              <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-lg p-4">
                <div className="bg-gray-900/50 rounded-md w-10 h-10 flex items-center justify-center mb-3">
                  {React.cloneElement(feature.icon, { className: "h-4 w-4 text-cyan-400" })}
                </div>
                <h3 className="text-base font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="relative bg-gradient-to-r from-gray-900 to-blue-900 py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-3">Ready to Get Started?</h2>
          <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto">
            Join thousands of companies already using our platform to build
            production-ready machine learning models.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center bg-white text-gray-900 px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-100 transition-colors"
          >
            Start Free Trial
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;